import { useState, useEffect, FormEvent } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { invalidateSiteSettings } from '@/hooks/useSiteSettings';

const sha256 = async (text: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

const CURRENCIES = [
  { code: 'CHF', label: 'CHF — İsviçre Frangı', symbol: 'CHF' },
  { code: 'EUR', label: 'EUR — Euro', symbol: '€' },
  { code: 'USD', label: 'USD — Amerikan Doları', symbol: '$' },
  { code: 'GBP', label: 'GBP — İngiliz Sterlini', symbol: '£' },
  { code: 'TRY', label: 'TRY — Türk Lirası', symbol: '₺' },
  { code: 'SEK', label: 'SEK — İsveç Kronası', symbol: 'kr' },
  { code: 'NOK', label: 'NOK — Norveç Kronası', symbol: 'kr' },
  { code: 'DKK', label: 'DKK — Danimarka Kronası', symbol: 'kr' },
  { code: 'PLN', label: 'PLN — Polonya Zlotısı', symbol: 'zł' },
  { code: 'CZK', label: 'CZK — Çek Korunası', symbol: 'Kč' },
];

const LANGUAGES = [
  { code: 'de', label: 'Deutsch (Almanca)' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English (İngilizce)' },
  { code: 'fr', label: 'Français (Fransızca)' },
  { code: 'it', label: 'Italiano (İtalyanca)' },
  { code: 'ar', label: 'العربية (Arapça)' },
  { code: 'nl', label: 'Nederlands (Hollandaca)' },
  { code: 'pl', label: 'Polski (Lehçe)' },
];

const TIMEZONES = [
  'Europe/Zurich',
  'Europe/Berlin',
  'Europe/Istanbul',
  'Europe/London',
  'Europe/Paris',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Warsaw',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Dubai',
];

const COUNTRIES = [
  { code: 'CH', label: 'İsviçre' },
  { code: 'DE', label: 'Almanya' },
  { code: 'AT', label: 'Avusturya' },
  { code: 'TR', label: 'Türkiye' },
  { code: 'GB', label: 'İngiltere' },
  { code: 'FR', label: 'Fransa' },
  { code: 'IT', label: 'İtalya' },
  { code: 'NL', label: 'Hollanda' },
  { code: 'BE', label: 'Belçika' },
  { code: 'PL', label: 'Polonya' },
  { code: 'US', label: 'Amerika Birleşik Devletleri' },
  { code: 'AE', label: 'Birleşik Arap Emirlikleri' },
];

type MsgState = { type: 'success' | 'error'; text: string } | null;

const SettingsTab = () => {
  // Security
  const [adminPw, setAdminPw] = useState('');
  const [adminPwConfirm, setAdminPwConfirm] = useState('');
  const [adminMsg, setAdminMsg] = useState<MsgState>(null);
  const [savingAdmin, setSavingAdmin] = useState(false);

  // Regional
  const [currency, setCurrency] = useState('CHF');
  const [language, setLanguage] = useState('de');
  const [taxRate, setTaxRate] = useState('8');
  const [country, setCountry] = useState('CH');
  const [timezone, setTimezone] = useState('Europe/Zurich');
  const [regionalMsg, setRegionalMsg] = useState<MsgState>(null);
  const [savingRegional, setSavingRegional] = useState(false);

  // Setup info
  const [setupMsg, setSetupMsg] = useState<MsgState>(null);
  const [loadingSettings, setLoadingSettings] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('site_settings').select('key, value');
      if (data) {
        const map: Record<string, string> = {};
        data.forEach((r) => { map[r.key] = r.value ?? ''; });
        if (map.currency) setCurrency(map.currency);
        if (map.language) setLanguage(map.language);
        if (map.tax_rate) setTaxRate(map.tax_rate);
        if (map.country) setCountry(map.country);
        if (map.timezone) setTimezone(map.timezone);
      }
      setLoadingSettings(false);
    };
    load();
  }, []);

  const upsertSetting = async (key: string, value: string) => {
    await supabase.from('site_settings').upsert({ key, value }, { onConflict: 'key' });
  };

  const handleAdminPwChange = async (e: FormEvent) => {
    e.preventDefault();
    if (!adminPw || adminPw !== adminPwConfirm) {
      setAdminMsg({ type: 'error', text: 'Şifreler eşleşmiyor.' });
      return;
    }
    if (adminPw.length < 6) {
      setAdminMsg({ type: 'error', text: 'Şifre en az 6 karakter olmalıdır.' });
      return;
    }
    setSavingAdmin(true);
    const hash = await sha256(adminPw);
    const { error } = await supabase
      .from('kitchen_config')
      .update({ admin_password_hash: hash })
      .neq('id', '00000000-0000-0000-0000-000000000000');
    setSavingAdmin(false);
    if (error) {
      setAdminMsg({ type: 'error', text: 'Kayıt sırasında hata oluştu.' });
    } else {
      setAdminMsg({ type: 'success', text: 'Yönetici şifresi başarıyla güncellendi.' });
      setAdminPw('');
      setAdminPwConfirm('');
    }
  };

  const handleSaveRegional = async () => {
    setSavingRegional(true);
    setRegionalMsg(null);
    try {
      const selectedCurrency = CURRENCIES.find((c) => c.code === currency);
      await Promise.all([
        upsertSetting('currency', currency),
        upsertSetting('currency_symbol', selectedCurrency?.symbol ?? currency),
        upsertSetting('language', language),
        upsertSetting('tax_rate', taxRate),
        upsertSetting('country', country),
        upsertSetting('timezone', timezone),
      ]);
      invalidateSiteSettings();
      setRegionalMsg({ type: 'success', text: 'Bölgesel ayarlar kaydedildi. Değişiklikler siteye yansıdı.' });
    } catch {
      setRegionalMsg({ type: 'error', text: 'Kayıt sırasında hata oluştu.' });
    } finally {
      setSavingRegional(false);
    }
  };

  const handleExportSetup = () => {
    const setupData = {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      instructions: 'Bu dosyayı yeni bir kurulumda Supabase site_settings tablosuna aktarın.',
      settings: {
        currency,
        language,
        tax_rate: taxRate,
        country,
        timezone,
      },
      supabase_tables: ['homepage_sections', 'categories', 'site_settings', 'products', 'extras', 'admin_sessions', 'kitchen_config', 'hero_slides', 'system_config'],
      pages: ['/', '/menu', '/admin'],
    };
    const blob = new Blob([JSON.stringify(setupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'restaurant-setup.json';
    a.click();
    URL.revokeObjectURL(url);
    setSetupMsg({ type: 'success', text: 'Kurulum dosyası indirildi.' });
    setTimeout(() => setSetupMsg(null), 3000);
  };

  if (loadingSettings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 max-w-2xl">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Ayarlar</h2>
        <p className="text-gray-500 text-sm">Güvenlik, bölgesel ve kurulum ayarlarını yönetin</p>
      </div>

      {/* ── BÖLGESEL AYARLAR ── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
            <i className="ri-global-line text-amber-500 text-lg"></i>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Bölgesel Ayarlar</h3>
            <p className="text-xs text-gray-500">Para birimi, dil, vergi oranı ve saat dilimi</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Para Birimi */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Para Birimi</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all cursor-pointer bg-white"
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Menü fiyatlarında gösterilir</p>
          </div>

          {/* Dil */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Arayüz Dili</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all cursor-pointer bg-white"
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Müşteri arayüzünün dili</p>
          </div>

          {/* Vergi Oranı */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Vergi Oranı (%)</label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="30"
                step="0.1"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                className="w-full px-4 py-2.5 pr-10 rounded-xl border border-gray-200 text-sm text-gray-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">Bilgi amaçlı (vitrin sitesi)</p>
          </div>

          {/* Ülke */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Ülke</label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all cursor-pointer bg-white"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Saat Dilimi */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Saat Dilimi</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all cursor-pointer bg-white"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Çalışma saatleri ve içerik için referans</p>
          </div>
        </div>

        {regionalMsg && (
          <div className={`mt-4 flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg ${
            regionalMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
          }`}>
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className={regionalMsg.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
            </div>
            {regionalMsg.text}
          </div>
        )}

        <button
          onClick={handleSaveRegional}
          disabled={savingRegional}
          className="mt-5 self-start px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
        >
          {savingRegional ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-save-line"></i>
            </div>
          )}
          Bölgesel Ayarları Kaydet
        </button>
      </div>

      {/* ── KURULUM ── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center">
            <i className="ri-store-2-line text-green-600 text-lg"></i>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Kurulum & Dağıtım</h3>
            <p className="text-xs text-gray-500">Bu sistemi başka restoranlara kurmak için</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Kurulum adımları */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wider">Yeni Restoran Kurulum Adımları</p>
            <div className="space-y-2.5">
              {[
                { step: '1', text: 'Supabase\'de yeni proje oluştur', icon: 'ri-database-2-line' },
                { step: '2', text: 'SQL şemasını yeni projeye aktar (tüm tablolar)', icon: 'ri-file-code-line' },
                { step: '3', text: '.env dosyasına yeni Supabase URL ve ANON KEY gir', icon: 'ri-key-line' },
                { step: '4', text: 'Admin panelinden restoran bilgilerini güncelle', icon: 'ri-settings-3-line' },
                { step: '5', text: 'Menü kategorilerini ve ürünlerini ekle', icon: 'ri-restaurant-line' },
                { step: '6', text: 'Bölgesel ayarları (para birimi, dil) yapılandır', icon: 'ri-global-line' },
              ].map((item) => (
                <div key={item.step} className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                    <span className="text-green-700 text-xs font-bold">{item.step}</span>
                  </div>
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-gray-400 text-xs`}></i>
                  </div>
                  <span className="text-xs text-gray-600">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Kurulum dosyası indir */}
          <div className="border border-dashed border-gray-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-xl flex-shrink-0">
                <i className="ri-download-2-line text-gray-600 text-lg"></i>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-800 mb-0.5">Kurulum Yapılandırma Dosyası</p>
                <p className="text-xs text-gray-500 mb-3">
                  Mevcut ayarları JSON formatında indir. Yeni kurulumda referans olarak kullan.
                </p>
                <button
                  onClick={handleExportSetup}
                  className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-all flex items-center gap-2"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-download-line"></i>
                  </div>
                  restaurant-setup.json İndir
                </button>
                {setupMsg && (
                  <p className={`text-xs mt-2 font-medium ${setupMsg.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                    {setupMsg.text}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Sistem bilgisi */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Sistem Versiyonu', value: 'v1.0.0', icon: 'ri-code-s-slash-line' },
              { label: 'Veritabanı', value: 'Supabase PostgreSQL', icon: 'ri-database-line' },
              { label: 'Frontend', value: 'React + TypeScript', icon: 'ri-reactjs-line' },
              { label: 'Stil', value: 'Tailwind CSS', icon: 'ri-palette-line' },
            ].map((info) => (
              <div key={info.label} className="bg-gray-50 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${info.icon} text-gray-400 text-xs`}></i>
                  </div>
                  <span className="text-xs text-gray-400">{info.label}</span>
                </div>
                <p className="text-xs font-semibold text-gray-700">{info.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── YÖNETİCİ ŞİFRESİ ── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
            <i className="ri-shield-keyhole-line text-orange-500 text-lg"></i>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Yönetici Şifresi</h3>
            <p className="text-xs text-gray-500">
              Giriş e-postası: <span className="font-medium text-gray-700">info@yawas-kebab.ch</span>
              {' · '}Admin paneli giriş şifresini değiştirin
            </p>
          </div>
        </div>
        <form onSubmit={handleAdminPwChange} className="flex flex-col gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Yeni Şifre</label>
            <input
              type="password"
              value={adminPw}
              onChange={(e) => { setAdminPw(e.target.value); setAdminMsg(null); }}
              placeholder="En az 6 karakter"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Şifreyi Onayla</label>
            <input
              type="password"
              value={adminPwConfirm}
              onChange={(e) => { setAdminPwConfirm(e.target.value); setAdminMsg(null); }}
              placeholder="Şifreyi tekrar girin"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all"
            />
          </div>
          {adminMsg && (
            <div className={`flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg ${
              adminMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
            }`}>
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className={adminMsg.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
              </div>
              {adminMsg.text}
            </div>
          )}
          <button
            type="submit"
            disabled={savingAdmin}
            className="self-start px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
          >
            {savingAdmin ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-save-line"></i>
              </div>
            )}
            Kaydet
          </button>
        </form>
      </div>

      {/* Güvenlik bilgisi */}
      <div className="bg-orange-50 rounded-2xl border border-orange-100 p-5">
        <div className="flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-orange-500"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-orange-800 mb-1">Güvenlik Bilgisi</p>
            <p className="text-xs text-orange-700 leading-relaxed">
              Yönetici şifresi SHA-256 ile şifrelenerek saklanır. Düz metin olarak hiçbir yerde tutulmaz.
              Oturum 4 saat sonra otomatik olarak sona erer.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
